"use client";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import currencify from "@/utils/currencify";
import { formatPricePerSqft } from "@/utils/currency/get-rate";
import { formatRange } from "@/utils/format-range";
import { SectionContent, SectionTitle } from "../seller-unit/section-components";
import { SpaceItem } from "../seller-unit/space-item";

interface OverviewSectionProps {
	supOne: SellerUnitsAndProjectsOne;
	isLoading?: boolean;
	className?: string;
}

interface OverviewItem {
	icon: string;
	label: string;
	value: string;
	available: boolean | undefined;
	tooltip?: React.ReactNode;
}

export function OverviewSection({ supOne, className }: OverviewSectionProps) {
	// Helper function to check if value is not just zeros
	const hasNonZeroValues = (data: number[] | undefined) => {
		return data && data.length > 0 && data.some(val => val > 0);
	};

	const items: OverviewItem[] = [
		{
			icon: "bedroom",
			label: "Bedrooms",
			value: formatRange({ data: supOne.bedrooms, withSpaces: false }),
			available: hasNonZeroValues(supOne.bedrooms),
		},
		{
			icon: "bathroom",
			label: "Bathrooms",
			value: formatRange({ data: supOne.bathrooms, withSpaces: false }),
			available: hasNonZeroValues(supOne.bathrooms),
		},
		{
			icon: "kitchen",
			label: "Kitchen",
			value: formatRange({ data: supOne.kitchens, withSpaces: false }),
			available: hasNonZeroValues(supOne.kitchens),
		},
		{
			icon: "hall",
			label: "Hall",
			value: formatRange({ data: supOne.halls, withSpaces: false }),
			available: hasNonZeroValues(supOne.halls),
		},
		{
			icon: "parking",
			label: "Parking",
			value: formatRange({ data: supOne.parkings, withSpaces: false }),
			available: hasNonZeroValues(supOne.parkings),
		},
		{
			icon: "balcony",
			label: "Balcony",
			value: formatRange({ data: supOne.balconies, withSpaces: false }),
			available: hasNonZeroValues(supOne.balconies),
		},
		{
			icon: "builtup-area",
			label: "Builtup Area",
			value: formatRange({ data: supOne.builtUpAreas, withSpaces: false }) + " sft",
			available: !!supOne.builtUpAreas?.length,
		},
	];

	const priceNum = supOne.prices?.[0];
	const priceStr = priceNum ? currencify({ amount: priceNum, abbreviated: true }) : "";
	const rate = supOne.currentRate
		? Math.round(supOne.currentRate).toLocaleString()
		: formatPricePerSqft({ price: priceNum || 0, area: supOne.builtUpAreas?.[0] || 0 });

	return (
		<SectionContent className={`${className} flex flex-col`}>
			<SectionTitle>
				<div className="flex items-center justify-between">
					<div>Overview</div>
					{priceStr && (
						<div className="text-right font-bold">
							<div className="text-lg text-primary">{`₹${priceStr}` + (supOne.type === "sp" ? "+" : "")}</div>
							{rate && <div className="text-xs">{rate} /sft</div>}
						</div>
					)}
				</div>
			</SectionTitle>
			<div className="flex justify-evenly items-center pt-8 pb-4 flex-wrap gap-4 gap-y-8 flex-1">
				{items
					.filter(item => item.available)
					.map((item, index) => (
						<SpaceItem
							key={index}
							label={item.label}
							available={item.available ?? false}
							icon={item.icon}
							value={item.value?.toString() || ""}
							tooltip={item.tooltip}
						/>
					))}
			</div>
		</SectionContent>
	);
}
